import type { RegionCode } from "@/api/filter";
import { configureGroupKey } from "@/utils/configure-group-key";
import {
  resolveConfiguredMachinePriceOverride,
  resolveRecommendedConfigurationTotal,
} from "@/utils/configured-investment";
import { isTSeriesMachineId } from "@/utils/tseries-split";

const CONFIGURE_OVERRIDE_CONCURRENCY = 4;

/** ceil(Σ per-app machine utilisation) for a subset on one machine — the same
 * required-machines the comparison footer uses (`requiredMachinesForAssignment`). */
function subsetRequiredMachines(
  utilByApp: Record<string, number> | undefined,
  subset: readonly string[],
): number {
  let util = 0;
  for (const appId of subset) {
    if (!utilByApp || !Object.prototype.hasOwnProperty.call(utilByApp, appId)) {
      throw new Error(
        `Cannot optimize allocation: machine utilization is not ready for application ${appId}.`,
      );
    }
    const value = Number(utilByApp[appId]);
    if (!Number.isFinite(value) || value < 0) {
      throw new Error(
        `Cannot optimize allocation: machine utilization is invalid for application ${appId}.`,
      );
    }
    util += value;
  }
  return Math.max(1, Math.ceil(util));
}

/**
 * CR-006: the FULL configured investment per (machine, app-subset) for optimize
 * scoring — the SAME value the comparison "Totals" footer shows
 * (`resolveRecommendedConfigurationTotal`), computed at the subset's required
 * machines with the user's manual quantities. Sent as
 * `configuredInvestmentByGroupKey`; the solver scores it once. This replaces the
 * old single-machine `machinePriceOverrideByGroupKey × requiredMachines` path,
 * whose over-scaling made Optimize minimise a different total than the footer.
 * `configureAppIds` mirrors the footer's rule: a split T-Series prices its BOM
 * against the whole compare list; every other family uses just the subset.
 */
export async function buildConfiguredInvestmentByGroupKey(args: {
  productIds: string[];
  applicationIds: string[];
  region: RegionCode;
  mspAutoFeedByProductId: Record<string, boolean>;
  splitEnabled: boolean;
  utilByMachineApp: Record<string, Record<string, number>>;
  /** Product-scoped manual quote rows, counted once whenever the product is used. */
  manualLineTotalByProductId?: Readonly<Record<string, number>>;
  signal?: AbortSignal;
}): Promise<Record<string, string>> {
  if (args.signal?.aborted) {
    throw args.signal.reason ?? new DOMException("Aborted", "AbortError");
  }
  const controller = new AbortController();
  const abortFromParent = () => controller.abort(args.signal?.reason);
  args.signal?.addEventListener("abort", abortFromParent, { once: true });
  const out: Record<string, string> = {};
  const subsets = allNonemptySubsets(args.applicationIds);
  const jobs = args.productIds.flatMap((productId) =>
    subsets.map((subset) => ({ productId, subset })),
  );
  let nextJobIndex = 0;
  let stopped = false;
  let firstError: unknown;

  try {
    await Promise.all(
      Array.from(
        { length: Math.min(CONFIGURE_OVERRIDE_CONCURRENCY, jobs.length) },
        async () => {
          while (!stopped && nextJobIndex < jobs.length) {
            const job = jobs[nextJobIndex];
            nextJobIndex += 1;
            if (!job) return;
            const { productId, subset } = job;
            try {
              const configureAppIds =
                args.splitEnabled && isTSeriesMachineId(productId)
                  ? args.applicationIds
                  : subset;
              const requiredMachines = subsetRequiredMachines(
                args.utilByMachineApp[productId],
                subset,
              );
              const manualLineTotal =
                args.manualLineTotalByProductId?.[productId] ?? 0;
              if (!Number.isFinite(manualLineTotal) || manualLineTotal < 0) {
                throw new Error(
                  `Cannot optimize allocation: manual-line total is invalid for product ${productId}.`,
                );
              }
              const investment = await resolveRecommendedConfigurationTotal({
                productId,
                region: args.region,
                applicationIds: configureAppIds,
                mspAutoFeedEnabled:
                  args.mspAutoFeedByProductId[productId] ?? true,
                requiredMachines,
                signal: controller.signal,
              });
              const configuredTotal = Number(investment);
              if (
                !investment ||
                !Number.isFinite(configuredTotal) ||
                configuredTotal < 0
              ) {
                throw new Error(
                  `Cannot optimize allocation: configured investment is unavailable for product ${productId}.`,
                );
              }
              // Match the comparison footer exactly: configured catalog investment
              // plus product-scoped manual quote rows, each counted once for the
              // product group (never once per app or per required machine).
              out[configureGroupKey(productId, subset)] = (
                configuredTotal + manualLineTotal
              ).toFixed(4);
            } catch (error) {
              if (!stopped) {
                stopped = true;
                firstError = error;
                controller.abort(error);
              }
              throw firstError;
            }
          }
        },
      ),
    );
    return out;
  } finally {
    args.signal?.removeEventListener("abort", abortFromParent);
  }
}

/** Every non-empty subset of `items` (POC optimize configure memo keys). */
export function allNonemptySubsets<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  const n = items.length;
  for (let mask = 1; mask < 1 << n; mask += 1) {
    const subset: T[] = [];
    for (let i = 0; i < n; i += 1) {
      if (mask & (1 << i)) subset.push(items[i]!);
    }
    out.push(subset);
  }
  return out;
}

/** POC subset configure prices for optimize scoring. */
export async function buildConfigureOverrideByGroupKey(args: {
  productIds: string[];
  applicationIds: string[];
  region: RegionCode;
  mspAutoFeedByProductId: Record<string, boolean>;
  signal?: AbortSignal;
}): Promise<Record<string, string>> {
  if (args.signal?.aborted) {
    throw args.signal.reason ?? new DOMException("Aborted", "AbortError");
  }
  const controller = new AbortController();
  const abortFromParent = () => controller.abort(args.signal?.reason);
  args.signal?.addEventListener("abort", abortFromParent, { once: true });
  const out: Record<string, string> = {};
  const subsets = allNonemptySubsets(args.applicationIds);
  const jobs = args.productIds.flatMap((productId) =>
    subsets.map((subset) => ({ productId, subset })),
  );
  let nextJobIndex = 0;
  let stopped = false;
  let hasError = false;
  let firstError: unknown;

  try {
    await Promise.all(
      Array.from(
        { length: Math.min(CONFIGURE_OVERRIDE_CONCURRENCY, jobs.length) },
        async () => {
          while (!stopped && nextJobIndex < jobs.length) {
            const job = jobs[nextJobIndex];
            nextJobIndex += 1;
            if (!job) return;

            const { productId, subset } = job;
            let price: string | undefined;
            try {
              price = await resolveConfiguredMachinePriceOverride({
                productId,
                region: args.region,
                applicationIds: subset,
                mspAutoFeedEnabled:
                  args.mspAutoFeedByProductId[productId] ?? true,
                requiredMachines: 1,
                signal: controller.signal,
              });
            } catch (error) {
              if (!hasError) {
                hasError = true;
                firstError = error;
                stopped = true;
                controller.abort(error);
              }
              throw firstError;
            }
            if (price) {
              out[configureGroupKey(productId, subset)] = price;
            }
          }
        },
      ),
    );
    return out;
  } finally {
    args.signal?.removeEventListener("abort", abortFromParent);
  }
}
